import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useHistory } from 'react-router-dom';
import { apiService } from '../services/api';

// Import Components
import AddBanner from '../components/AddBanner';
import AddProperty from '../components/AddProperty';
import AddService from '../components/AddService';
import DeleteProperty from '../components/DeleteProperty';
import DeleteBanner from '../components/DeleteBanner';
import DeleteExperts from '../components/DeleteExperts';
import ApproveSubmissions from '../components/ApproveSubmissions';

// ===== TAB PANEL COMPONENT =====
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ===== TAB CATEGORIES CONFIGURATION =====
const TAB_CATEGORIES = [
  {
    category: 'CREATE',
    icon: <AddCircleIcon sx={{ mr: 1 }} />,
    color: '#10b981',
    tabs: [
      {
        id: 0,
        label: '🖼️ Add Banner',
        title: 'Add New Banner',
        description: 'Create and upload new banners',
        component: AddBanner,
      },
      {
        id: 1,
        label: '🏠 Add Property',
        title: 'Add New Property',
        description: 'Create and list new properties',
        component: AddProperty,
      },
      {
        id: 2,
        label: '👤 Add Service',
        title: 'Add New Service/Expert',
        description: 'Add new services or expert profiles',
        component: AddService,
      },
    ],
  },
  {
    category: 'DELETE',
    icon: <DeleteIcon sx={{ mr: 1 }} />,
    color: '#ef4444',
    tabs: [
      {
        id: 3,
        label: '🗑️ Delete Property',
        title: 'Delete Properties',
        description: 'Remove properties from database',
        component: DeleteProperty,
      },
      {
        id: 4,
        label: '🗑️ Delete Banner',
        title: 'Delete Banners',
        description: 'Remove banners from database',
        component: DeleteBanner,
      },
      {
        id: 5,
        label: '🗑️ Delete Service',
        title: 'Delete Services/Experts',
        description: 'Remove services or expert profiles',
        component: DeleteExperts,
      },
    ],
  },
  {
    category: 'APPROVE',
    icon: <CheckCircleIcon sx={{ mr: 1 }} />,
    color: '#3b82f6',
    tabs: [
      {
        id: 6,
        label: '📋 Approve All',
        title: 'Approve Pending Submissions',
        description: 'Review and approve pending submissions',
        component: ApproveSubmissions,
      },
    ],
  },
];

// ===== FLAT TABS FOR INTERNAL USE =====
const getAllTabs = () => {
  return TAB_CATEGORIES.flatMap(cat => cat.tabs);
};

// ===== MAIN COMPONENT =====
const AdminDashboard = ({ onLogout }) => {
  const history = useHistory();
  const [adminUser, setAdminUser] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [categoryView, setCategoryView] = useState(false);

  const allTabs = getAllTabs();

  // ===== EFFECTS =====
  useEffect(() => {
    checkAdminAccess();
  }, [history]);

  // ===== HELPER FUNCTIONS =====
  const checkAdminAccess = async () => {
    try {
      console.log('🔐 Checking admin access...');
      
      const token = apiService.getAuthToken();
      console.log('🔑 Token found:', token ? `${token.substring(0, 20)}...` : 'None');

      if (!token) {
        console.log('❌ No token found, redirecting to login');
        history.push('/login');
        return;
      }

      console.log('🔍 Verifying token with backend...');
      const response = await apiService.verifyToken();
      console.log('✅ Token verification response:', response);

      if (!response.success) {
        console.log('❌ Token verification failed:', response.message);
        history.push('/login');
        return;
      }

      const user = apiService.getAdminUser();
      console.log('👤 Admin user:', user);

      if (!user) {
        console.log('⚠️ No admin user in storage, but token is valid');
        setAdminUser({ id: 'admin', name: 'Administrator' });
      } else {
        setAdminUser(user);
      }

      setLoading(false);
    } catch (error) {
      console.error('❌ Admin access check failed:', error);
      showAlert(`Error: ${error.message}`, 'error');
      history.push('/login');
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCategoryTabClick = (tabIndex) => {
    setTabValue(tabIndex);
    setCategoryView(false);
  };

  const handleLogout = async () => {
    try {
      console.log('🚪 Initiating logout...');
      showAlert('Logging out...', 'info');
      
      await apiService.logout();
      
      setAdminUser(null);
      
      if (onLogout) onLogout();
      
      console.log('✅ Logged out successfully');
      history.push('/login');
    } catch (error) {
      console.error('❌ Logout error:', error);
      setAdminUser(null);
      if (onLogout) onLogout();
      history.push('/login');
    }
  };

  // ===== LOADING STATE =====
  if (loading) {
    return (
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 6, 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ mt: 3, fontSize: '1.1rem', fontWeight: 500 }}>
            Loading admin dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  // ===== UNAUTHORIZED STATE =====
  if (!adminUser) {
    return (
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: 6, 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2, fontWeight: 700 }}>
            🔒 Unauthorized Access
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            You don't have permission to access this page.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => history.push('/login')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  const currentTab = allTabs[tabValue];
  const TabComponent = currentTab.component;

  // ===== CATEGORY VIEW RENDER =====
  if (categoryView) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        {/* TOP APP BAR */}
        <AppBar 
          position="sticky" 
          elevation={2}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Toolbar>
            <DashboardIcon sx={{ mr: 2, fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
                Admin Dashboard
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Welcome, <strong>{adminUser.name || adminUser.id || 'Admin'}</strong>
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{ 
                px: 2.5,
                fontWeight: 700,
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(211, 47, 47, 0.3)',
                }
              }}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        {/* ALERTS */}
        {alert && (
          <Container maxWidth="lg" sx={{ pt: 3 }}>
            <Alert 
              severity={alert.severity} 
              onClose={() => setAlert(null)}
              sx={{ mb: 2 }}
            >
              {alert.message}
            </Alert>
          </Container>
        )}

        {/* CATEGORIES VIEW */}
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 1, sm: 2 } }}>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 800, 
                color: '#2d3748',
                mb: 2
              }}
            >
              📊 Select Operation
            </Typography>
            <Typography 
              variant="body1" 
              color="textSecondary"
              sx={{ mb: 3 }}
            >
              Choose a category to manage your content
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {TAB_CATEGORIES.map((category, categoryIndex) => (
              <Grid item xs={12} sm={6} md={4} key={categoryIndex}>
                <Card 
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: `2px solid ${category.color}20`,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 24px ${category.color}30`,
                      borderColor: category.color,
                    }
                  }}
                  onClick={() => {
                    setTabValue(category.tabs[0].id);
                    setCategoryView(false);
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ color: category.color, display: 'flex', alignItems: 'center' }}>
                        {category.icon}
                      </Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 800, 
                          color: category.color,
                          ml: 1
                        }}
                      >
                        {category.category}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Sub-tabs in this category */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {category.tabs.map((tab) => (
                        <Typography 
                          key={tab.id}
                          variant="body2"
                          sx={{
                            p: 1,
                            borderLeft: `3px solid ${category.color}`,
                            pl: 2,
                            borderRadius: 1,
                            bgcolor: `${category.color}10`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: `${category.color}20`,
                              pl: 2.5,
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategoryTabClick(tab.id);
                          }}
                        >
                          {tab.label}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* FOOTER */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4, 
            color: '#718096',
            mt: 4
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>
            🕐 Last verified: {new Date().toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
            © 2024 Admin Dashboard. All rights reserved.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ===== NORMAL TAB VIEW RENDER =====
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* TOP APP BAR */}
      <AppBar 
        position="sticky" 
        elevation={2}
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar>
          <DashboardIcon sx={{ mr: 2, fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
              Admin Dashboard
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Welcome, <strong>{adminUser.name || adminUser.id || 'Admin'}</strong>
            </Typography>
          </Box>
          <Button
            variant="text"
            sx={{ mr: 2, color: '#fff', fontWeight: 600 }}
            onClick={() => setCategoryView(true)}
          >
            📊 Categories
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ 
              px: 2.5,
              fontWeight: 700,
              '&:hover': {
                boxShadow: '0 8px 16px rgba(211, 47, 47, 0.3)',
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {/* ALERTS */}
      {alert && (
        <Container maxWidth="lg" sx={{ pt: 3 }}>
          <Alert 
            severity={alert.severity} 
            onClose={() => setAlert(null)}
            sx={{ mb: 2 }}
          >
            {alert.message}
          </Alert>
        </Container>
      )}

      {/* MAIN CONTENT */}
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 1, sm: 2 } }}>
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2 }}>
          {/* CATEGORY TABS */}
          <Box sx={{ bgcolor: '#f9fafb', px: 2, py: 1.5, borderBottom: '2px solid #e0e0e0' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea', textTransform: 'uppercase', letterSpacing: 1 }}>
              📂 Operation Categories
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              {TAB_CATEGORIES.map((category) => {
                const categoryTabs = category.tabs;
                const isActive = categoryTabs.some(t => t.id === tabValue);
                return (
                  <Box key={category.category}>
                    <Chip
                      icon={category.icon}
                      label={category.category}
                      onClick={() => {
                        setTabValue(categoryTabs[0].id);
                      }}
                      variant={isActive ? 'filled' : 'outlined'}
                      sx={{
                        bgcolor: isActive ? category.color : 'transparent',
                        color: isActive ? '#fff' : category.color,
                        borderColor: category.color,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* TABS HEADER */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: '2px solid #e0e0e0',
              bgcolor: '#ffffff',
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '0.95rem',
                minHeight: 56,
                '&:hover': {
                  bgcolor: '#f0f0f0',
                }
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#667eea',
              }
            }}
          >
            {allTabs.map((tab) => (
              <Tab key={tab.id} label={tab.label} value={tab.id} />
            ))}
          </Tabs>

          {/* TAB CONTENT */}
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: '#fff' }}>
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#2d3748',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {currentTab.title}
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary"
                sx={{ mt: 1 }}
              >
                {currentTab.description}
              </Typography>
              <Divider sx={{ mt: 2, mb: 4 }} />
            </Box>

            <TabPanel value={tabValue} index={tabValue}>
              <TabComponent
                onSuccess={() => showAlert('✅ Action completed successfully!', 'success')}
                onError={(msg) => showAlert(`❌ ${msg}`, 'error')}
                adminUserId={adminUser.id}
              />
            </TabPanel>
          </Box>
        </Paper>

        {/* FOOTER */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4, 
            color: '#718096',
            mt: 4
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>
            🕐 Last verified: {new Date().toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
            © 2024 Admin Dashboard. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AdminDashboard;

